import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Typography, List, Switch, Menu, Collapse, Button, Tooltip, Spin, Empty } from "antd";
import { LayoutFilled, SaveFilled, InfoCircleFilled, WarningFilled, ProductFilled } from '@ant-design/icons';
import { createAssignments } from "../../../../API/assignments";
import ControlsAssignmentTable from "./components/ControlsAssignmentTable/ControlsAssignmentTable"; 
import ControlsFilter from "./components/ControlsFilter/ControlsFilter";

const { Panel } = Collapse;
const { Title } = Typography;

const AssignmentControls = ({
  assets,
  loading,
  selectedAsset,
  selectedDependency,
  assignmentsControlsToAssets,
  assignmentHistory,
  setSelectedAsset,
  setSelectedDependency,
  setAssignmentsControlsToAssets,
  setAssignmentHistory,
  priorities,
  types,
  api,
  registerSaveFunction,
  permits
}) => {

  const [selectAll, setSelectAll] = useState(false); 
  const [searchTerm, setSearchTerm] = useState('');
  // Estado para la seleccion desde la vista de Activos
  const [selectedAssetKey, setSelectedAssetKey] = useState(null);
  const [selectedDependencyKey, setSelectedDependencyKey] = useState(null);

  // Filtros
  const [filters, setFilters] = useState({
    priority: null,
    type: null,
    enabled: null,
    selectAllDisabled: false // Nuevo estado
  });

  // Función de filtrado completa
  const filteredControls = selectedDependency 
    ? selectedDependency.controls.filter(control => {
      const matchesSearch = control.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesPriority = !filters.priority || control.priority?.name === filters.priority;
      const matchesType = !filters.type || control.type?.name === filters.type;
      const matchesEnabled = filters.enabled === null || control.flag === filters.enabled;
      
      return matchesSearch && matchesPriority && matchesType && matchesEnabled;
    })
  : [];

  // Función para manejar el cambio de estado de "Asignar todos"
  /* const onSelectAllChange = (e) => {
  // Verificación adicional para filteredControls
  if (!selectedAsset || !selectedDependency || filters.selectAllDisabled || !filteredControls.length) return;

  setAssignmentsControlsToAssets(prevAssignments => {
    const currentAssignments = prevAssignments[selectedAsset.key]?.[selectedDependency.key] || {};
    
    const newAssignments = filteredControls.reduce((acc, control) => {
      // Solo asignamos si no está ya asignado (optimización)
      if (currentAssignments[control.code] !== true) {
        acc[control.code] = true;
      }
      return acc;
    }, {});

    // Si no hay cambios, no actualizamos el estado
    if (Object.keys(newAssignments).length === 0) {
      return prevAssignments;
    }

    const updatedAssignments = {
      ...prevAssignments,
      [selectedAsset.key]: {
        ...prevAssignments[selectedAsset.key],
        [selectedDependency.key]: {
          ...currentAssignments,
          ...newAssignments
        },
      },
    };

    // Actualización del historial
    const updatedHistory = filteredControls
      .filter(control => newAssignments[control.code] === true)
      .map(control => ({
        dependency_code: selectedDependency.key,
        control_code: control.code,
        is_active: true,
        asset: selectedAsset?.title || null,
      }));

    setAssignmentHistory(prev => [
      ...prev.filter(entry => 
        !filteredControls.some(
          control => control.code === entry.control_code && 
                    entry.dependency_code === selectedDependency.key
        )
      ),
      ...updatedHistory
    ]);
    
    // Deshabilitar el checkbox
    setFilters(prev => ({
      ...prev,
      selectAllDisabled: true,
      selectAllChecked: true
    }));

    return updatedAssignments;
  });
  }; */

  const onSelectAllChange = (e) => {
    // Verificación adicional
    if (!selectedAsset || !selectedDependency || filters.selectAllDisabled || !filteredControls.length) return;

    setAssignmentsControlsToAssets(prevAssignments => {
      const currentAssignments = prevAssignments[selectedAsset.key]?.[selectedDependency.key] || {};

      // Filtrar solo los controles que se pueden asignar (can_assign === true)
      const assignableControls = filteredControls.filter(control => control.can_assign);

      const newAssignments = assignableControls.reduce((acc, control) => {
        if (currentAssignments[control.code] !== true) {
          acc[control.code] = true;
        }
        return acc;
      }, {});

      if (Object.keys(newAssignments).length === 0) return prevAssignments;

      const updatedAssignments = {
        ...prevAssignments,
        [selectedAsset.key]: {
          ...prevAssignments[selectedAsset.key],
          [selectedDependency.key]: {
            ...currentAssignments,
            ...newAssignments
          },
        },
      };

      // Actualizar historial solo con los assignables
      const updatedHistory = assignableControls
        .filter(control => newAssignments[control.code] === true)
        .map(control => ({
          dependency_code: selectedDependency.key,
          control_code: control.code,
          is_active: true,
          asset: selectedAsset?.title || null,
        }));

      setAssignmentHistory(prev => [
        ...prev.filter(entry => 
          !assignableControls.some(
            control => control.code === entry.control_code && 
                      entry.dependency_code === selectedDependency.key
          )
        ),
        ...updatedHistory
      ]);

      // Deshabilitar el switch "Seleccionar todos" después de usarlo
      setFilters(prev => ({
        ...prev,
        selectAllDisabled: true,
        selectAllChecked: true
      }));

      return updatedAssignments;
    });
  };

  const onAssetSelect = (assetCode) => {
    const selectedAsset = assets.find(asset => asset.key === assetCode);

    if (selectedAsset) {
      setSelectedAsset(selectedAsset);
      setSelectedDependency(null); 
    }
  };

  /* const onDependencySelect = (dependencyCode) => {
    if (!selectedAsset) return;

    const selectedDependency = selectedAsset.dependencies.find(dep => dep.key === dependencyCode);

    if (selectedDependency) {
      setSelectedDependency({
        ...selectedDependency,
        controls: selectedDependency.controls || [],
      });
    }
  }; */

  const onDependencySelect = (dependencyCode) => {
    if (!selectedAsset) return;
  
    const selectedDependency = selectedAsset.dependencies.find(dep => dep.key === dependencyCode);
  
    if (selectedDependency) {
      setSelectedDependency({
        ...selectedDependency,
        controls: selectedDependency.controls || [],
      });
      
      // Resetear el estado del checkbox al cambiar de dependencia
      setFilters(prev => ({
        ...prev,
        selectAllDisabled: false
      }));
    }
  };

  const onControlSwitchChange = (checked, controlCode) => {
    if (!selectedAsset || !selectedDependency) return;

    setAssignmentsControlsToAssets(prevAssignments => {
      const updatedAssignments = {
        ...prevAssignments,
        [selectedAsset.key]: {
          ...prevAssignments[selectedAsset.key],
          [selectedDependency.key]: {
            ...prevAssignments[selectedAsset.key]?.[selectedDependency.key],
            [controlCode]: checked,
          },
        },
      };

      const existingEntryIndex = assignmentHistory.findIndex(
        entry =>
          entry.dependency_code === selectedDependency.key &&
          entry.control_code === controlCode
      );

      let updatedHistory;

      if (existingEntryIndex >= 0) {
        updatedHistory = assignmentHistory.map((entry, index) =>
          index === existingEntryIndex
            ? { ...entry, is_active: checked }
            : entry
        );
      } else {
        updatedHistory = [
          ...assignmentHistory,
          {
            dependency_code: selectedDependency.key,
            control_code: controlCode,
            is_active: checked,
            asset: selectedAsset ? selectedAsset.title : null,
          },
        ];
      }

      setAssignmentHistory(updatedHistory);

      return updatedAssignments;
    });
  };

  const onSaveAssignments = useCallback(() => {
    let allChangedControls = [];
  
    // Recorrer todos los activos
    Object.keys(assignmentsControlsToAssets).forEach(assetKey => {
      const asset = assets.find(a => a.key === assetKey);
      if (!asset) return;
  
      // Recorrer todas las dependencias del activo
      Object.keys(assignmentsControlsToAssets[assetKey]).forEach(depKey => {
        const dependency = asset.dependencies.find(d => d.key === depKey);
        if (!dependency) return;
  
        const currentControls = assignmentsControlsToAssets[assetKey][depKey] || {};
        
        // Obtener los valores iniciales
        const initialControls = dependency.controls.reduce((acc, control) => {
          acc[control.code] = control.flag;
          return acc;
        }, {});
  
        // Filtrar los controles modificados
        const changedControls = Object.keys(currentControls)
          .filter(controlCode => currentControls[controlCode] !== initialControls[controlCode])
          .map(controlCode => ({
            dependency_code: depKey,
            control_code: controlCode,
            flag: currentControls[controlCode],
            asset: asset.title // Opcional: incluir información del activo
          }));
  
        allChangedControls = [...allChangedControls, ...changedControls];
      });
    });
  
    if (allChangedControls.length === 0) {
      api.info({ 
        message: "No se han realizado cambios en las asignaciones",
        key: 'change-info'
      });
      return;
    }
  
    createAssignments(allChangedControls)
      .then(() => {
        api.success({ 
          message: "Asignaciones guardadas con éxito",
          description: `Se ${allChangedControls.length === 1 ? 'actualizó' : 'actualizaron'} ${allChangedControls.length} control${allChangedControls.length === 1 ? '' : 'es'}`
        }); 
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      })
      .catch((error) => {
        api.error({ message: "Error al guardar asignaciones." });
        console.error("Error en createAssignments:", error);
      });
  }, [assignmentsControlsToAssets, assets, api]);

  const getControlFlag = (controlCode) => {
    if (!selectedAsset || !selectedDependency) return false;
    return assignmentsControlsToAssets[selectedAsset.key]?.[selectedDependency.key]?.[controlCode] ?? false;
  };

  // ControlsFilter
  const allFilteredAssigned = selectedDependency && filteredControls.length > 0 
  ? filteredControls.every(control => 
      assignmentsControlsToAssets[selectedAsset.key]?.[selectedDependency.key]?.[control.code] === true
    )
  : false;

  const someFilteredUnassigned = selectedDependency && filteredControls.length > 0
    ? filteredControls.some(control =>
        assignmentsControlsToAssets[selectedAsset.key]?.[selectedDependency.key]?.[control.code] !== true
      )
    : false;

  const hasNoMatchingControls = selectedDependency && 
    (filteredControls.length === 0) && 
    (searchTerm || filters.priority || filters.type || filters.enabled !== null);
  
  // Estado para controlar cuando no hay resultados
  const hasNoControlsAtAll = selectedDependency && selectedDependency.controls.length === 0;

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      selectAllDisabled: allFilteredAssigned || hasNoMatchingControls || hasNoControlsAtAll,
      selectAllChecked: (hasNoMatchingControls || hasNoControlsAtAll) ? false : !someFilteredUnassigned
    }));
  }, [allFilteredAssigned, someFilteredUnassigned, hasNoMatchingControls, hasNoControlsAtAll]);

  // Efecto para el manejo de selección visual
  useEffect(() => {
    if (selectedAsset) {
      setSelectedAssetKey(selectedAsset.key);
    } else {
      setSelectedAssetKey(null);
    }
    
    if (selectedDependency) {
      setSelectedDependencyKey(selectedDependency.key);
    } else {
      setSelectedDependencyKey(null);
    }
  }, [selectedAsset, selectedDependency]);

  // En AssignmentControls.jsx
  useEffect(() => {
    if (registerSaveFunction) {
      registerSaveFunction(onSaveAssignments);
    }
  }, [onSaveAssignments]);

  return (
    <>
      <Row gutter={16} className="assignment-form" style={{ height: 400 }}>
        <Col span={5}>
          <Title level={4}>Activos</Title>
          <Spin className="custom-spin" spinning={loading}>
          {!loading && assets.length === 0 ? (
            <Empty 
              description="No hay activos disponibles o no se definieron dependencias para los activos"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <div className="scrollable-list-container">
              <Menu 
                className="custom-assignment-menu" 
                selectedKeys={selectedAssetKey ? [selectedAssetKey] : []}
                onClick={({ key }) => {
                  onAssetSelect(key);
                  setSelectedAssetKey(key);
                  setSelectedDependencyKey(null);
                }}
                mode="inline"
              >
                {assets.map((asset) => (
                  <Menu.Item key={asset.key} icon={<LayoutFilled />}>
                    {asset.title}
                  </Menu.Item>
                ))}
              </Menu>
            </div>
          )}
          </Spin>
        </Col>
        <Col span={5}>
          <Title level={4}>Dependencias</Title>
          {selectedAsset ? (
            <div className="scrollable-list-container">
              <Menu 
                className="custom-assignment-menu" 
                selectedKeys={selectedDependencyKey ? [selectedDependencyKey] : []}
                onClick={({ key }) => {
                  onDependencySelect(key);
                  setSelectedDependencyKey(key);
                }}
                mode="inline">
                {selectedAsset.dependencies.map((dependency) => (
                  <Menu.Item key={dependency.key} icon={<LayoutFilled />}>
                    <Tooltip title={dependency.name.length > 20 && dependency.name}>
                      <span>{dependency.name}</span>
                    </Tooltip>
                  </Menu.Item>
                ))}
              </Menu>
            </div>
          ) : (
            <p>Seleccione un activo para ver las dependencias.</p>
          )}
        </Col>
        <Col span={14}>
          <Row>
            <Col span={3}> 
              <Title level={4}>Controles</Title>
            </Col>
            </Row>
            <Row>
              <Col span={24} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {selectedDependency && (
                  <ControlsFilter
                    searchTerm={searchTerm}
                    filters={{
                      ...filters,
                      selectAllDisabled: allFilteredAssigned || filters.selectAllDisabled
                    }}
                    onSearchChange={(e) => setSearchTerm(e.target.value)}
                    onFiltersChange={setFilters}
                    onSelectAllChange={onSelectAllChange}
                    priorities={priorities}
                    types={types}
                  />
                )}
              </Col>
          </Row>   
          {selectedDependency ? (
            filteredControls.length > 0 ? (
              <div className="scrollable-list-container">
                <List
                  className="custom-assignment-list"
                  dataSource={filteredControls}
                  renderItem={(control) => {
                    return (
                      <List.Item
                        actions={[
                          <Switch
                            className="custom-assignment-switch"
                            disabled={
                              getControlFlag(control.code) !== control.flag
                                ? (getControlFlag(control.code) ? !control.can_assign : !control.can_unassign)
                                : (control.flag ? !control.can_unassign : !control.can_assign)
                            }
                            key={control.code}
                            checked={getControlFlag(control.code)}
                            onChange={(checked) => onControlSwitchChange(checked, control.code)}
                          />,
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            <Collapse
                              defaultActiveKey={[]}
                              expandIconPosition="end"
                              style={{ border: "1px solid #d9d9d9", borderRadius: 8, overflow: 'hidden' }}
                            >
                              <Panel
                                key="1"
                                header={
                                  <div style={{ display: "flex", alignItems: "center" }}>
                                    <Tooltip title={`Tipo: ${control.type.name || "No especificado"}`}>
                                      <ProductFilled style={{ fontSize: 18, color: "#006939", marginRight: 5 }} />
                                    </Tooltip>
                                    <Tooltip title={`Prioridad: ${control.priority.name || "No especificado"}`}>
                                      <WarningFilled 
                                        style={{ fontSize: 18, color: "#006939", marginRight: 5, color:
                                                control.priority.name === 'Alta' ? '#ff4d4f' :
                                                control.priority.name === 'Media' ? '#faad14' :
                                                control.priority.name === 'Baja' ? '#52c41a' :
                                                'transparent'}} 
                                      />
                                    </Tooltip>
                                    <Title level={5} style={{ margin: 0 }}>{control.title}</Title>
                                  </div>
                                }
                              >
                                {control.description || "Descripción del control no disponible"}
                              </Panel>
                            </Collapse>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </div>
            ) : (
              <p style={{ textAlign: 'center', padding: '20px' }}>
                {searchTerm 
                  ? "No se encontraron controles que coincidan con la búsqueda" 
                  : "No hay controles disponibles para esta dependencia"}
              </p>
            )
          ) : (
            <p>
              Seleccione una dependencia para ver los controles
            </p>
          )}
        </Col>
      </Row>

      <Row style={{ marginTop: 10 }}>
        <Col span={24} >
          <ControlsAssignmentTable
            assignmentsControlsToAssets={assignmentsControlsToAssets}
            assets={assets}
          />
        </Col>
        <Col span={24} style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
              icon={<SaveFilled />}
              className="save-type-button"
              type="primary"
              htmlType="button"
              onClick={onSaveAssignments}
              disabled={
                assets.length === 0 ||
                !(
                  permits.isSuperuser ||
                  (permits.hasAnyRole && permits.canAssignmentCreate || permits.canAssignmentUpdate)
                )
              }
            >
              Guardar
            </Button>
        </Col>
      </Row>
    </>
  );
};

export default AssignmentControls;